/**
 * Das Ablaufen der Bewegungspfade — gemeinsame Grundlage von Bewegung und Schlagen.
 *
 * Warum geteilt: §21 definiert Schlagen als „legal auf das Feld des Gegners **ziehen**".
 * Angriff und Bewegung benutzen also dieselben Wege, nur mit unterschiedlicher Reichweite
 * und unterschiedlichem Ziel. Stünde die Pfadlogik zweimal im Code, müsste jede Regel —
 * Blockade, Überspringen, Ringbewegung, Zielfeld-Barriere — an zwei Stellen gepflegt
 * werden, und die beiden Stellen würden irgendwann auseinanderlaufen.
 *
 * Abgedeckt sind die zwei Bewegungsarten, die sich in einem Zug nicht mischen lassen:
 * die sechs geraden Hex-Achsen und der Ring (nur, wenn die Figur ihren Zug auf einem
 * Ringfeld beginnt, mit festem Umlaufsinn je Lauf).
 *
 * **Bestätigt am 2026-10-02** (`docs/OPEN_QUESTIONS.md` Nr. 17): auch die
 * *Schlagreichweite* zählt entlang des Rings. Es folgt daraus, dass Schlagen ein Ziehen ist
 * und die Ringbewegung eine Bewegungsart — eine Sonderregel „Ring nur für MOVE" stünde
 * nirgends.
 */
import { isOnBoard } from './board.js';
import { pieceAt } from './board-state.js';
import { goalCellBlocking } from './goal-cell.js';
import { HEX_DIRECTIONS, hexAdd } from './hex.js';
import { isRingCell, ringNeighbor } from './ring.js';
const RING_DIRECTIONS = ['forward', 'backward'];
/**
 * Läuft einen einzelnen Pfad ab, bis er endet, und meldet jedes betretene Feld.
 *
 * Der Lauf endet am Brettrand, an einer harten Zielfeld-Barriere und — für Figuren ohne
 * `canJump` — an der ersten Figur. Das blockierende Feld wird **noch gemeldet**: beim
 * Schlagen ist genau dieses Feld das Ziel.
 */
function walkOnePath(context, piece, range, jumping, nextCell, visit) {
    const limit = range.max ?? Number.POSITIVE_INFINITY;
    let current = piece.at;
    for (let step = 1; step <= limit; step++) {
        current = nextCell(current);
        if (current === undefined)
            break;
        if (!isOnBoard(context.board, current))
            break;
        const occupant = pieceAt(context.state, current);
        if (step >= range.min)
            visit({ cell: current, step, occupant });
        const blocking = goalCellBlocking(context.config, current, piece.type);
        // Eine harte Barriere stoppt auch einen überspringenden Springer.
        if (blocking.hard)
            break;
        if (!jumping && (occupant !== undefined || blocking.soft))
            break;
    }
}
/**
 * Läuft alle Pfade dieser Figur ab: sechs gerade Achsen, dazu beide Umlaufrichtungen des
 * Rings, falls die Figur auf einem Ringfeld steht und der Ring aktiv ist.
 */
export function walkPaths(context, piece, range, jumping, visit) {
    for (const direction of HEX_DIRECTIONS) {
        walkOnePath(context, piece, range, jumping, (from) => hexAdd(from, direction), visit);
    }
    if (context.config.ring.enabled && isRingCell(context.board, piece.at)) {
        for (const direction of RING_DIRECTIONS) {
            walkOnePath(context, piece, range, jumping, (from) => ringNeighbor(context.board, from, direction), visit);
        }
    }
}
//# sourceMappingURL=path-walk.js.map